import React, {
  useEffect,
  useState,
  useMemo,
  forwardRef,
  useCallback,
} from "react";
import { Form } from "antd";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes, parseSelectOptions } from "utils/formUtils";
import message from "components/CustomMessage";
import GenericMappingForm from "../GenericMappingForm";
import _ from "lodash";

const SynopsysPolarisMappingForm = forwardRef(({ disableProjectFields = false, ...props }, ref) => {
  const [projectList, setProjectList] = useState({});
  const [portfolioList, setPortfolioList] = useState([]);
  const [portfolio, setPortfolio] = useState("");
  const [form] = Form.useForm();

  useEffect(() => {
    const { promise, cancel } =
      ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
        login_id: props.loginConfigId,
      });
    promise
      .then((response) => {
        const { projects } = response;
        setPortfolioList(projects);
      })
      .catch(() => {
        message.error("Problem fetching projects");
      });

    return cancel;
  }, [props.loginConfigId, props.toolName]);

  const fetchProjects = useCallback(
    (project) => {
      if (!projectList[project] && project) {
        const { promise, cancel } =
          ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, {
            login_id: props.loginConfigId,
            project,
          });

        promise
          .then((response) => {
            const { projects } = response;
            setProjectList((projectList) => ({
              ...projectList,
              [project]: projects,
            }));
          })
          .catch(() => {
            message.error("Problem fetching projects");
          });

        return cancel;
      }
    },
    [props.loginConfigId, props.toolName, projectList]
  );

  useEffect(() => {
    return fetchProjects(portfolio);
  }, [portfolio, fetchProjects]);

  const parseExtraFieldsDataFromResponse = useCallback((config) => {
    const { portfolioItemId } = config;
    const index = portfolioItemId.indexOf(':');
    const portfolioId = index !== -1 ? portfolioItemId.substring(0, index) : portfolioItemId;
    const project = index !== -1 ? portfolioItemId.substring(index + 1, portfolioItemId.length) : '';
    
    setPortfolio(portfolioId);
    return {
      portfolioItemId: portfolioId,
      project: _.isEmpty(project) ? [] : project.split(';'),
      //send the property received in config
      ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
    };
  }, []);

  const onProjectChange = useCallback(
    (value, field) => {
      setPortfolio(value);
      const { name } = field;
      const configurations = form.getFieldValue("configurations");
      configurations[name] = {
        ...configurations[name],
        project: undefined,
      };
      form.setFieldsValue({ configurations: configurations });
    },
    [form]
  );

  const getExtraFieldsData = useCallback(
    (config) => {
      const { portfolioItemId, project } = config;
      const portfolioItemName = portfolioList.find((project) => project.id === portfolioItemId)
        ?.name;
      const data = projectList[portfolioItemId].reduce((acc, cv) => {
        const { id, name } = cv;
        if((project || []).includes(id)){
          acc.id.push(id);
          acc.name.push(name);
        }
        return acc;
      },{id: [], name: [] })
      
      return {
        portfolioItemId: _.isEmpty(data.id) ? portfolioItemId : portfolioItemId + ":" + data.id.join(';'),
        portfolioItemName: _.isEmpty(data.name) ? portfolioItemName : portfolioItemName + ":" + data.name.join(';'),
        ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id })
      };
    },
    [portfolioList, projectList]
  );

  const extraFields = useMemo(
    () => [
      {
        name: "portfolioItemId",
        colSpan: "10",
        type: FieldTypes.SELECT,
        label: "Portfolio",
        disabled: disableProjectFields,
        options: parseSelectOptions(portfolioList),
        required: true,
        props: {
          onChange: onProjectChange,
          autoFocus: !disableProjectFields
        },
      },
      {
        name: "project",
        colSpan: "13",
        type: FieldTypes.MULTI_SELECT,
        label: "Project",
        disabled: disableProjectFields,
        loading: portfolio && !projectList[portfolio],
        props: {
          getFormListOptions: (fieldKey) => {
            const configs = form.getFieldValue("configurations");
            const portfolioItemId = configs[fieldKey]?.portfolioItemId;
            return parseSelectOptions(projectList[portfolioItemId]);
          },
        },
      },
    ],
    [portfolio, onProjectChange, projectList, portfolioList, form, disableProjectFields]
  );

  return (
    <GenericMappingForm
      form={form}
      ref={ref}
      getExtraFieldsData={getExtraFieldsData}
      extraFields={extraFields}
      parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
      {...props}
    />
  );
});

export default SynopsysPolarisMappingForm;
